<template>
    <div class="insured-table">
        <div class="insured-table-toolbar">
            <div class="insured-table-toolbar__controls">
                <div class="insured-search-group">
                    <el-select
                        v-model="active"
                        :placeholder="$t('policyAll')"
                        class="insured-search-group__select"
                        @change="onSearch"
                    >
                        <el-option :label="$t('policyAll')" :value="null" />
                        <el-option :label="$t('policyActive')" :value="true" />
                        <el-option :label="$t('policyTerminated')" :value="false" />
                    </el-select>
                    <el-input
                        v-model="srcText"
                        class="insured-search-group__input"
                        :placeholder="$t('policySearchPlaceholder')"
                        clearable
                        @clear="onSearch"
                        @keydown.enter="onSearch"
                    />
                </div>

                <button type="button" class="action-btn action-btn--success insured-table-toolbar__btn insured-table-toolbar__btn--search" @click="onSearch">
                    <i class="bi bi-search" aria-hidden="true"></i>
                    <span>{{ $t("policySearch") }}</span>
                </button>
                <button
                    type="button"
                    class="action-btn action-btn--icon insured-table-toolbar__btn insured-table-toolbar__btn--filter"
                    :class="showFilter ? 'action-btn--info is-active' : 'action-btn--muted'"
                    @click="showFilter = !showFilter"
                >
                    <i class="bi bi-funnel-fill" aria-hidden="true"></i>
                </button>
                <button
                    type="button"
                    class="action-btn action-btn--excel action-btn--icon insured-table-toolbar__btn"
                    @click="$emit('excel')"
                >
                    <i class="bi bi-file-earmark-excel-fill" aria-hidden="true"></i>
                </button>
            </div>

            <transition name="slide">
                <div v-show="showFilter" class="insured-table-filters">
                    <div class="insured-table-filters__item insured-table-filters__item--sm">
                        <label class="form-label">{{ $t("commonProgram") }}</label>
                        <el-select v-model="groupId" :placeholder="$t('commonAll')" clearable @change="onSearch">
                            <el-option :label="$t('commonAll')" :value="null" />
                            <el-option v-for="item in groupOptions" :key="item.groupId" :label="item.groupName"
                                :value="item.groupId" />
                        </el-select>
                    </div>
                    <div class="insured-table-filters__item insured-table-filters__item--lg">
                        <label class="form-label">{{ $t("commonStartDate") }}</label>
                        <el-date-picker v-model="date" type="daterange" unlink-panels range-separator="To"
                            :start-placeholder="$t('commonStartDate')" format="DD.MM.YYYY" :end-placeholder="$t('commonEndDate')"
                            :shortcuts="shortcuts" style="width: 100%;" @change="onSearch" />
                    </div>
                    <div class="insured-table-filters__item insured-table-filters__item--sm">
                        <label class="form-label">{{ $t("commonRelation") }}</label>
                        <el-select v-model="isEmployee" :placeholder="$t('commonAll')" clearable @change="onSearch">
                            <el-option :label="$t('commonAll')" :value="null" />
                            <el-option :label="$t('commonEmployee')" :value="true" />
                            <el-option :label="$t('commonFamilyMember')" :value="false" />
                        </el-select>
                    </div>
                </div>
            </transition>
        </div>
        <div class="table-responsive insured-table-wrap">
        <el-table size="small" border class="el-table-css" :data="insureds" style="width: 100%;">
            <el-table-column :label="$t('commonFullName')" min-width="180">
                <template #default="scope">
                    <a class="table-link" @click="info(scope.row)">{{
                        scope.row.insuredName }}</a>
                </template>
            </el-table-column>
            <el-table-column prop="vip" label="VIP" width="60" align="center">
                <template #default="{ row }">
                    <span v-if="row.vip" class="badge bg-warning text-dark fw-bold" style="font-size: 0.7rem; padding: 2px 6px;">VIP</span>
                    <span v-else class="text-muted text-xs">—</span>
                </template>
            </el-table-column>
            <el-table-column v-if="!isShort" prop="sexStr" :label="$t('commonSex')" width="70" />
            <el-table-column v-if="!isShort" :label="$t('commonBirthDate')" width="100">
                <template #default="scope">
                    <div class="text-xs">
                        {{ scope.row.dob ? scope.row.dob.slice(0, -9) : '' }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column v-if="!isShort" prop="address" :label="$t('commonAddress')" />
            <el-table-column v-if="!isShort" prop="phoneMobile" :label="$t('commonPhoneShort')" />
            <el-table-column prop="employeeStatusName" :label="$t('commonStatus')" width="80" />
            <el-table-column prop="cardFullNumber" :label="$t('commonCard')" width="80" />
            <el-table-column prop="groupName" :label="$t('commonProgram')" width="130" />
            <el-table-column prop="startDateStr" :label="$t('commonStart')" width="110" />
            <el-table-column prop="endDateStr" :label="$t('commonEnd')" width="110" />
            <el-table-column prop="cancelDateStr" :label="$t('commonTerminate')" width="110" />

        </el-table>
        </div>
        <div class="insured-table-pagination">
            <el-pagination :current-page="internalPage" :page-size="maxRow" background
                layout="prev, pager, next" :total="totalItems" @current-change="handleCurrentChange" />
            <p class="insured-table-pagination__count text-sm mb-0">
                {{ $t("commonCount") }}: {{ totalItems }}
            </p>
        </div>

        <request-operation-modal
            v-model="opModalVisible"
            :op-type="opModalType"
            :policy-id="effectivePolicyId"
            :initial-selected-insured="selectedInsuredForOp"
            @success="onOperationSuccess"
        />
    </div>
</template>

<script>
import { getPolicyId, insuredHref } from "@/project/Source/routes";

import {
    Search
} from '@element-plus/icons-vue'
import Source from "../../Source/claim.js";
import RequestOperationModal from "@/project/Request/components/RequestOperationModal.vue";

export default {
    name: "ClaimTable",
    components: {
        RequestOperationModal,
    },
    props: {
        isShort: {
            type: Boolean,
            default: false
        },
        tableData: {
            type: Array,
            default: () => []
        },
        maxRow: {
            type: Number,
            default: 10
        },
        insuredCount: {
            type: Number,
            default: 0
        },
        currentPage: {
            type: Number,
            default: 1
        },
        page: {
            type: Number,
            default: 1
        }
    },
    emits: ["get-table", "excel"],
    data() {
        return {
            insureds: this.tableData || [],
            Search,
            newData: Object,
            showFilter: null,
            search: '',
            srcText: null,
            isEmployee: null,
            active: null,
            totalItems: this.insuredCount || 0,
            loading: false,
            internalPage: this.currentPage || this.page || 1,
            date: [],
            groupId: null,
            groupOptions: [],
            opModalVisible: false,
            opModalType: "updateInfo",
            selectedInsuredForOp: null,
        };
    },
    computed: {
        effectivePolicyId() {
            return getPolicyId(this.$route) || 0;
        },
    },
    watch: {
        tableData(val) {
            this.insureds = val || [];
        },
        insuredCount(val) {
            this.totalItems = val || 0;
        },
        currentPage(val) {
            if (val) this.internalPage = val;
        },
        page(val) {
            if (val) this.internalPage = val;
        },
    },
    mounted() {
        const data = {
            "id": getPolicyId(this.$route),
            "requestCorrelationId": "string"
        }
        Source.Groups(data).then((response) => {
            this.groupOptions = response.data.data
        })
    },
    methods: {
        info(row) {
            const url = insuredHref(row.cardId, row.policyId);
            window.open(url, '_blank');
        },
        handleCurrentChange(page) {
            this.internalPage = page;
            const dateFrom = this.date && this.date[0] ? this.date[0] : null;
            const dateTo = this.date && this.date[1] ? this.date[1] : null;
            this.$emit('get-table', page, dateFrom, dateTo, this.isEmployee, this.groupId, this.active, this.srcText);
        },
        onSearch() {
            this.handleCurrentChange(1);
        },
        openActionModal(type, row) {
            this.opModalType = type;
            this.selectedInsuredForOp = row;
            this.opModalVisible = true;
        },
        onOperationSuccess() {
            this.handleCurrentChange(this.internalPage);
        },
    },
};
</script>

<style scoped>
.insured-row-actions {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.insured-action-sq-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid transparent;
    cursor: pointer;
    font-size: 0.8125rem;
    transition: all 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.insured-action-sq-btn--edit {
    background: color-mix(in srgb, #3b82f6 12%, transparent);
    border-color: color-mix(in srgb, #3b82f6 30%, transparent);
    color: #2563eb;
}

.insured-action-sq-btn--edit:hover {
    background: #2563eb;
    border-color: #2563eb;
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(37, 99, 235, 0.25);
}

.insured-action-sq-btn--program {
    background: color-mix(in srgb, #f59e0b 12%, transparent);
    border-color: color-mix(in srgb, #f59e0b 30%, transparent);
    color: #d97706;
}

.insured-action-sq-btn--program:hover {
    background: #d97706;
    border-color: #d97706;
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(217, 119, 6, 0.25);
}

.insured-action-sq-btn--terminate {
    background: color-mix(in srgb, #ef4444 12%, transparent);
    border-color: color-mix(in srgb, #ef4444 30%, transparent);
    color: #dc2626;
}

.insured-action-sq-btn--terminate:hover {
    background: #dc2626;
    border-color: #dc2626;
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(220, 38, 38, 0.25);
}
.insured-table {
    width: 100%;
    max-width: 100%;
}

.insured-table-toolbar,
.insured-table-wrap,
.insured-table-pagination {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0;
    margin-left: 0;
    margin-right: 0;
}

.insured-table-toolbar__controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
}

.insured-search-group {
    display: flex;
    align-items: stretch;
    flex: 1 1 280px;
    max-width: min(66%, 520px);
    min-width: 220px;
    border: 1px solid var(--app-border-strong);
    border-radius: var(--app-radius-sm);
    background: var(--app-surface);
    overflow: hidden;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
}

.insured-search-group:focus-within {
    border-color: color-mix(in srgb, var(--bs-info) 55%, var(--app-border-strong));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--bs-info) 14%, transparent);
}

.insured-search-group__select {
    width: 115px;
    flex: 0 0 115px;
}

.insured-search-group__input {
    flex: 1 1 auto;
    min-width: 0;
}

.insured-search-group :deep(.el-select__wrapper),
.insured-search-group :deep(.el-input__wrapper) {
    box-shadow: none !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    min-height: 36px;
    height: 36px;
}

.insured-search-group :deep(.el-select__wrapper) {
    border-right: 1px solid var(--app-border-strong) !important;
}

.insured-search-group :deep(.el-select__wrapper.is-focused),
.insured-search-group :deep(.el-input__wrapper.is-focus) {
    box-shadow: none !important;
}

.insured-table-toolbar__btn {
    flex: 0 0 auto;
    margin: 0 !important;
    min-height: 36px;
    box-shadow: none !important;
}

.insured-table-toolbar__btn--search {
    padding-left: 1rem;
    padding-right: 1rem;
}

.insured-table-toolbar__btn.action-btn--icon {
    min-width: 36px;
    padding-left: 0.55rem;
    padding-right: 0.55rem;
}

.insured-table-toolbar__btn.action-btn--icon i {
    font-size: 1.05rem;
}

.insured-table-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 0.75rem;
}

.insured-table-filters__item {
    min-width: 0;
}

.insured-table-filters__item--sm {
    flex: 1 1 140px;
    max-width: 200px;
}

.insured-table-filters__item--lg {
    flex: 1 1 260px;
    max-width: 360px;
}

.insured-table-wrap {
    margin-top: 0.85rem;
}

.insured-table-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 0.75rem;
}

.insured-table-pagination__count {
    color: var(--app-text);
    white-space: nowrap;
}
</style>